import type { TranslationKey } from "./_generated/translation-keys";
import type { DeepTypeable } from "./deepTypeable";
import type {
  IAddressModel,
  IBankDetailsModel,
  IBasicVaccinationModel,
  ICandidateOSModel,
  ICompetenciesModel,
  ICompetencyModel,
  IExperienceModel,
  IExternalCharacteristicsModel,
  IFurtherEducation,
  IInternalModel,
  ILanguageSkillModel,
  IOtherModel,
  IProcessStatusModel,
  IQualificationModel,
} from "./model/candidate.model";
import type { ICustomFieldsModel, ILanguageProgressTracking } from "./model/customFields.model";

//Replace a nested type in a nested type...
// stolen from https://stackoverflow.com/questions/51599481/replacing-property-of-a-typescript-type
type Identity<T> = { [P in keyof T]: T[P] };
type Replace<T, K extends keyof T, TReplace> = Identity<Pick<T, Exclude<keyof T, K>> & Record<K, TReplace>>;

type CustomMedwingFields = Replace<
  ICustomFieldsModel["medwing"],
  "languageProgressTracking",
  Omit<ILanguageProgressTracking, "id">
>;
type CustomFieldsWithConvertedArray = Replace<ICustomFieldsModel, "medwing", CustomMedwingFields>;

export const customFieldLabelMapping: DeepTypeable<CustomFieldsWithConvertedArray, TranslationKey> = {
  stFranziskus: {
    educationalAttribution: "customFields.educationalAttribution.title",
    dateOfCommission: "customFields.dateOfCommission.title",
    responsibleForOnboarding: "customFields.responsibleForOnboarding.title",
    vehicleForOnboarding: "customFields.vehicleForOnboarding.title",
    meetingPointForOnboarding: "customFields.meetingPointForOnboarding.title",
    pickupTime: "customFields.pickupTime.title",
  },
  talentCountry: {
    numberOfResidents: "customFields.talentCountrySpecific.numberOfResidents.title",
  },
  defa: {
    expectationWorkliveInGermany: "customFields.expectationWorkliveInGermany.title",
    whyStopWorkingForExistingEmployer: "customFields.whyStopWorkingForExistingEmployer.title",
    analysisScore: "customFields.analysisScore.title",
    assertivenessScore: "customFields.assertivenessScore.title",
    basicCalculationScore: "customFields.basicCalculationScore.title",
    communicationSkillScore: "customFields.communicationSkillScore.title",
    percentageCalculationScore: "customFields.percentageCalculationScore.title",
    spatialThinkingScore: "customFields.spatialThinkingScore.title",
    stressHandlungSkillScore: "customFields.stressHandlungSkillScore.title",
    teamworkSkillScore: "customFields.teamworkSkillScore.title",
  },
  dekra: {
    fakturState: "customFields.dekra.fakturState.title",
    fakturDate: "customFields.dekra.fakturDate.title",
    fakturType: "customFields.dekra.fakturType.title",
  },
  medwing: {
    languageProgressTracking: {
      comment: "customFields.languageProgressTracking.comment",
      module: "customFields.languageProgressTracking.module",
      presenceRatio: "customFields.languageProgressTracking.presenceRatio",
    },
  },
  gpCare: {
    interviewResponsible: "customFields.interviewResponsible.title",
  },
  amalCare: {
    simulationCourseCompleted: "customFields.simulationCourseCompleted.title",
  },
};

export const candidateLabelMapping: Record<keyof ICandidateOSModel, TranslationKey> & {
  noExperienceAvailable: TranslationKey;
  noFurtherEducationAvailable: TranslationKey;
  noQualificationsAvailable: TranslationKey;
  immigrationCountry: TranslationKey;
  recogNoticeAuthority: TranslationKey;
  recogNoticeFederalState: TranslationKey;
  recogNoticeFileNumber: TranslationKey;
  recogNoticePracticeHours: TranslationKey;
  recogNoticeReceived: TranslationKey;
  recogNoticeTheorieHours: TranslationKey;
  recogStarted: TranslationKey;
  recogSubmissionDate: TranslationKey;
  pathOfRecognition: TranslationKey;
  relocationRequirements: TranslationKey;
  externalCharacteristics: TranslationKey;
} = {
  title: "title.title",
  salutation: "salutation.title",
  firstname: "firstName.title",
  lastname: "lastName.title",
  birthname: "birthName.title",
  email: "email.title",
  phone: "phone.title",
  mobilePhone: "mobile.title",
  immigrationCountryPhone: "immigrationCountryPhone.title",
  birthdate: "birthDate.title",
  martialStatus: "maritalStatus.title",
  maritalStatusSince: "maritalStatusSince.title",
  numberOfChildren: "children.title",
  addresses: "address.title",
  citizenship: "citizenship.title",
  countryOfOrigin: "countryOfOrigin.title",
  countryOfHabitualResidence: "countryOfHabitualResidence.title",
  countryOfBirth: "countryOfBirth.title",
  cityOfBirth: "cityOfBirth.title",
  passNumber: "passNumber.title",
  passAuthority: "passAuthority.title",
  idCardNumber: "idCardNumber.title",
  taxNumber: "taxNumber.title",
  taxId: "taxId.title",
  healthInsuranceCompanyName: "healthInsuranceCompanyName.title",
  healthInsuranceNumber: "healthInsuranceNumber.title",
  hasLiabilityInsurance: "hasLiabilityInsurance.title",
  hasReceivedHealthInsuranceCard: "hasReceivedHealthInsuranceCard.title",
  liabilityInsuranceNumber: "liabilityInsuranceNumber.title",
  liabilityInsuranceCompanyName: "liabilityInsuranceCompanyName.title",
  hasAppliedForHealthInsurance: "hasAppliedForHealthInsurance.title",
  healthInsuranceAppliedDate: "healthInsuranceAppliedDate.title",
  healthInsuranceStartDate: "healthInsuranceStartDate.title",
  liabilityInsuranceAppliedDate: "liabilityInsuranceAppliedDate.title",
  liabilityInsuranceStartDate: "liabilityInsuranceStartDate.title",
  travelInsuranceCompanyName: "travelInsuranceCompanyName.title",
  travelInsuranceNumber: "travelInsuranceNumber.title",
  hasAppliedForTravelInsurance: "hasAppliedForTravelInsurance.title",
  travelInsuranceAppliedDate: "travelInsuranceAppliedDate.title",
  travelInsuranceStartDate: "travelInsuranceStartDate.title",
  travelInsuranceEndDate: "travelInsuranceEndDate.title",
  socSecNumber: "socSecNumber.title",
  drivingLicense: "drivingLicense.title",
  professionalDriver: "professionalDriver.title",
  qualifications: "qualification.title",
  experiences: "experience.title",
  languageSkills: "languageSkills.title",
  preferredCareFacility: "preferences.careFacility.title",
  preferredCareFacilityFlexibility: "preferences.careFacility.flexibility.title",
  preferredWorkEnvironment: "preferences.workEnvironment.title",
  preferredWorkEnvironmentFlexibility: "preferences.workEnvironment.flexibility.title",
  preferredProfessions: "preferences.preferredProfessions.title",
  preferredWorkingField: "preferences.field.title",
  preferredWorkingFieldFlexibility: "preferences.field.flexibility.title",
  preferredLocationState: "preferences.state.title",
  preferredLocationStateFlexibility: "preferences.state.flexibility.title",
  preferredCity: "preferences.city.title",
  preferredCityFlexibility: "preferences.city.flexibility.title",
  preferredCandidates: "preferences.candidates.title",
  preferredPathOfRecognition: "preferences.pathOfRecognition.title",
  preferredLanguageLevelForInterview: "preferences.languageLevelForInterview.title",
  preferredLanguageLevelForTransfer: "preferences.languageLevelForTransfer.title",
  preferredStartDate: "preferences.preferredStartDate.title",
  preferredEmploymentType: "preferences.employmentType.title",
  preferredTemporalScope: "preferences.temporalScope.title",
  preferredTypeOfImmigration: "preferences.preferredTypeOfImmigration.title",
  preferredFamilyReunion: "preferences.preferredFamilyReunion.title",
  furtherEducations: "furtherEducation.title",
  totalExperienceInMonth: "experience.total",
  // socSecNumber: "",
  vaccinations: "vaccinations.title",
  other: "other.title2",
  customFields: "",
  noQualificationsAvailable: "qualification.noQualificationsAvailable",
  noExperienceAvailable: "experience.noExperienceAvailable",
  noFurtherEducationAvailable: "experience.noFurtherEducationAvailable",
  immigrationCountry: "immigrationCountry.title",
  recogNoticeAuthority: "recognitionAuthority.title",
  recogNoticeFederalState: "federalState.recognition",
  recogNoticeFileNumber: "fileNumber.title",
  recogNoticePracticeHours: "recognition.practiceHours",
  recogNoticeReceived: "recognition.received",
  recogNoticeTheorieHours: "recognition.theoryHours",
  recogStarted: "recognition.started",
  recogSubmissionDate: "recognition.submissionDate",
  pathOfRecognition: "pathOfRecognition.title",
  relocationRequirements: "relocationRequirements.title",
  bankDetails: "bankDetails.title",
  competencies: "competencies.title",
  externalCharacteristics: "externalCharacteristics.title",
};

export const experienceLabelMapping: Record<keyof IExperienceModel, TranslationKey> = {
  id: "",
  experienceStartDate: "startOfExperience.title",
  experienceEndDate: "endOfExperience.title",
  experienceFields: "experienceFields.title",
  experienceFunction: "function.title",
  experienceFacility: "careFacility.title",
  experienceInstitution: "nameOfInstitution2.title",
  experienceBedCount: "beds.title",
  experienceCount: "experience.opPerMonth",
  experienceMonth: "experience.month",
  experienceWorkArea: "experienceWorkArea.title",
  experienceYears: "experience.years",
  city: "city.title",
  country: "country.title",
  experienceCoreTasks: "experience.coreTasks",
  occupation: "",
  occupationOther: "occupationOther.title",
  experienceType: "experienceType.title",
  otherActivity: "otherActivities.title",
  typeOfEmployment: "typeOfEmployment.title",
  mandatoryInternship: "mandatoryInternship.title",
  workHours: "workHours.title",
  hoursPerWeek: "hoursPerWeek.title",
  experienceCoreTasksDelta: "",
  employerAddress: "experience.employerContactInfo.address.title",
  employerPhone: "experience.employerContactInfo.phone.title",
  employerEmail: "experience.employerContactInfo.email.title",
};

export const languageSkillLabelMapping: Partial<Record<keyof ILanguageSkillModel, TranslationKey>> = {
  language: "language.title",
  skillLevel: "skillLevel.shortTitle",
  examCertificateDate: "examCertificateDate.title",
  examInstitution: "examInstitution.title",
  languageTrainingStatus: "languageTrainingStatus.title",
  languageLevelSource: "languageLevelSource.title",
};

export const qualificationLabelMapping: Record<keyof IQualificationModel, TranslationKey> = {
  id: "",
  qualification: "qualification.degree",
  nameOfInstitution: "nameOfInstitution.title",
  startOfTraining: "startOfTraining.title",
  endOfTraining: "endOfTraining.title",
  occupation: "occupation.title",
  professionalTitle: "professionalTitle.title",
  professionalTitleGermanTranslation: "professionalTitleGermanTranslation.title",
  occupationalTitle: "occupationalTitle.title",
  diplomaIssuance: "diplomaIssuance.title",
  addressOfInstitution: "addressOfInstitution.title",
  city: "city.title",
  country: "",
  finalGrade: "finalGrade.title",
  isRecognitionOrOccupationRelevant: "qualification.isRecognitionOrOccupationRelevant.title",
  educationFocus: "qualification.educationFocus.title",
  educationDurationInMonths: "qualification.educationDurationInMonths.title",
  practicalTrainingInMonths: "qualification.practicalTrainingInMonths.title",
  entailsTheoreticalTraining: "qualification.entailsTheoreticalTraining.title",
  entailsPracticalTraining: "qualification.entailsPracticalTraining.title",
  entailsCompanyBasedTraining: "qualification.entailsCompanyBasedTraining.title",
  entailsSchoolBasedTraining: "qualification.entailsSchoolBasedTraining.title",
  educationalInstitutionPhone: "qualification.educationalInstitutionContactInfo.phone.title",
  educationalInstitutionEmail: "qualification.educationalInstitutionContactInfo.email.title",
  educationalInstitutionWebsite: "qualification.educationalInstitutionContactInfo.website.title",
  issuingAuthorityName: "qualification.issuingAuthorityContactInfo.authorityName.title",
  issuingAuthorityAddress: "qualification.issuingAuthorityContactInfo.address.title",
  issuingAuthorityCity: "qualification.issuingAuthorityContactInfo.city.title",
  issuingAuthorityCountry: "qualification.issuingAuthorityContactInfo.country.title",
  issuingAuthorityPhone: "qualification.issuingAuthorityContactInfo.phone.title",
  issuingAuthorityEmail: "qualification.issuingAuthorityContactInfo.email.title",
  issuingAuthorityWebsite: "qualification.issuingAuthorityContactInfo.website.title",
};

export const furtherEducationLabelMapping: Record<keyof IFurtherEducation, TranslationKey> = {
  id: "",
  startDate: "furtherEducation.startDate.title",
  endDate: "furtherEducation.endDate.title",
  fieldOfEducation: "furtherEducation.fieldOfEducation.title",
  nameOfInstitution: "nameOfInstitution.title",
  totalHours: "furtherEducation.totalHours.title",
  description: "furtherEducation.description.title",
  nameOfEducation: "furtherEducation.nameOfEducation.title",
  notProfessionRelated: "furtherEducation.notProfessionRelated.title",
  educationalInstitutionAddress: "furtherEducation.educationalInstitutionContactInfo.address.title",
  educationalInstitutionPhone: "furtherEducation.educationalInstitutionContactInfo.phone.title",
  educationalInstitutionEmail: "furtherEducation.educationalInstitutionContactInfo.email.title",
  issuingAuthorityName: "furtherEducation.issuingAuthorityContactInfo.authorityName.title",
  issuingAuthorityAddress: "furtherEducation.issuingAuthorityContactInfo.address.title",
  issuingAuthorityCity: "furtherEducation.issuingAuthorityContactInfo.city.title",
  issuingAuthorityCountry: "furtherEducation.issuingAuthorityContactInfo.country.title",
  issuingAuthorityPhone: "furtherEducation.issuingAuthorityContactInfo.phone.title",
  issuingAuthorityEmail: "furtherEducation.issuingAuthorityContactInfo.email.title",
  issuingAuthorityWebsite: "furtherEducation.issuingAuthorityContactInfo.website.title",
};

export const processStatusLabelMapping: Record<keyof IProcessStatusModel, string> = {
  estimatedArrivalDate: "estimatedArrivalDate.title",
  estimatedWorkStartDate: "estimatedWorkStartDate.title",
};

export const addressLabelMapping: Record<keyof IAddressModel, TranslationKey> = {
  id: "",
  address: "address.title",
  residentSince: "residentSince.title",
  addressType: "addressType.title",
  city: "city.title",
  country: "country.title",
  region: "federalState.title",
  zipcode: "zipCode.title",
};

export const otherLabelMapping: Record<keyof IOtherModel, TranslationKey> = {
  wayToUs: "wayToUs.title",
  hobbies: "hobbies.title",
  socialSkills: "socialSkills.title",
  itSkills: "itSkills.title",
  familiarEquipment: "familiarEquipment.title",
  aboutMe: "aboutMe.title",
};

export const externalCharacteristicsLabelMapping: Record<keyof IExternalCharacteristicsModel, TranslationKey> = {
  eyeColor: "eyeColor.title",
  bodyHeight: "bodyHeight.title",
};

export const bankDetailsLabelMapping: Record<keyof IBankDetailsModel, TranslationKey> = {
  id: "",
  isSepa: "typeOfBankTransfer.title",
  isImmigrationCountryBankDetail: "countryTypeOfBankDetail.title",
  isCurrentAccount: "isCurrentAccount.title",
  accountHolder: "accountHolder.title",
  bankName: "bankName.title",
  iban: "iban.title",
  bic: "bic.title",
  bankAccountNumber: "bankAccountNumber.title",
  bankCountry: "bankCountry.title",
  currency: "currency.title",
  isBic: "typeOfBankTransfer.isBic",
  bankCode: "bankCode.title",
};

export const languageProgressTrackingLabelMapping = {
  assessmentTestDate: "languageTrainingTracking.languageProgressTracking.assessmentTestDate.title",
  assessmentTestResult: "languageTrainingTracking.languageProgressTracking.assessmentTestResult.title",
  assessmentTestSecondTryDate: "languageTrainingTracking.languageProgressTracking.assessmentTestSecondTryDate.title",
  assessmentTestSecondTryResult:
    "languageTrainingTracking.languageProgressTracking.assessmentTestSecondTryResult.title",
  level: "languageTrainingTracking.languageProgressTracking.level.title",
  module: "languageTrainingTracking.languageProgressTracking.module.title",
  presenceRatio: "languageTrainingTracking.languageProgressTracking.presenceRatio.title",
  statusLanguageCertificate: "languageTrainingTracking.languageProgressTracking.statusLanguageCertificate.title",
  statusLanguageCertificateDate:
    "languageTrainingTracking.languageProgressTracking.statusLanguageCertificateDate.title",
};

export const VaccinationLabelMapping: Record<keyof IBasicVaccinationModel, TranslationKey> = {
  id: "",
  vaccinations: "vaccinations.name.title",
  vaccine: "vaccinations.vaccine.title",
  comments: "vaccinations.comments.title",
  vaccinationStatus: "vaccinations.vaccinationStatus.title",
  plannedNextShot: "vaccinations.plannedNextShot.title",
  plannedFinalShot: "vaccinations.plannedFinalShot.title",
};

export const internalLabelMapping: Record<keyof IInternalModel, TranslationKey> = {
  costCenter: "costCenter.title",
  languageSchooldId: "partner.languageSchool",
  sourceOrganizationId: "partner.source",
  placementPartnerOrganizationIds: "partner.placement",
  potentialEmployerOrganizationIds: "potentialEmployer.title",
  preferredEmployerOrganizationIds: "preferredEmployer.title",
  category: "partner.category",
  notes: "candidate.notesFull",
};

export const competenciesLabelMapping: Record<keyof ICompetenciesModel, TranslationKey> = {
  knowledges: "knowledges.title",
  skills: "skills.title",
  customKnowledges: "customKnowledges.title",
  customSkills: "customSkills.title",
};

export const competencyLabelMapping: Record<keyof ICompetencyModel, TranslationKey> = {
  id: "",
  skillId: "skill.title",
  proficiency: "proficiency.title",
  additionalInformation: "additionalInformation.title",
};
